import React, {memo, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Copy, ThumbsUp, ThumbsDown, X} from 'lucide-react';
import {citationMarkdown} from '../ai-context.mjs';
import styles from './AiAssistant.module.css';

type Rating = 'like' | 'dislike' | null;
export type FeedbackBody = {messageId: string; feedbackToken: string; rating: Rating; content?: string};
type Props = {text: string; messageId?: string; feedbackToken?: string; zh: boolean; open: boolean; submit: (body: FeedbackBody) => void};
const reasons = [['incorrect', '不正确', 'Incorrect'], ['unhelpful', '没有帮助', 'Not helpful'], ['incomplete', '不完整', 'Incomplete'], ['verbose', '过于冗长', 'Too verbose'], ['other', '其他', 'Other']];

function FeedbackDialog({zh, dismiss, submit}: {zh: boolean; dismiss: () => void; submit: (content: string) => void}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    return () => {if (previous?.isConnected) previous.focus({preventScroll: true});};
  }, []);
  return createPortal(<div className={styles.feedbackOverlay} onClick={event => {if (event.target === event.currentTarget) dismiss();}}>
    <div ref={dialog} tabIndex={-1} className={styles.feedbackDialog} role="dialog" aria-modal="true" aria-label={zh ? '告诉我们更多' : 'Tell us more'} onKeyDown={event => {
      if (event.key === 'Escape') {event.stopPropagation(); dismiss();}
      if (event.key === 'Tab') {
        const controls = dialog.current?.querySelectorAll<HTMLElement>('button, textarea');
        if (!controls?.length) return;
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {event.preventDefault(); last.focus();}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus();}
      }
    }}>
      <header><h2>{zh ? '告诉我们更多' : 'Tell us more'}</h2><button type="button" aria-label={zh ? '关闭反馈' : 'Close feedback'} onClick={dismiss}><X size={18}/></button></header>
      <form onSubmit={event => {
        event.preventDefault();
        const labels = reasons.filter(([id]) => selected.includes(id)).map(item => item[zh ? 1 : 2]);
        submit([labels.length ? `[${labels.join('、')}]` : '', comment.trim()].filter(Boolean).join('\n'));
      }}>
        <div className={styles.feedbackFields}>
          <div className={styles.feedbackReasons}>{reasons.map(([id, cn, en]) => <button type="button" key={id} aria-pressed={selected.includes(id)} className={selected.includes(id) ? styles.selected : undefined} onClick={() => setSelected(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id])}>{zh ? cn : en}</button>)}</div>
          <textarea aria-label={zh ? '反馈评论（可选，最多100字符）' : 'Feedback comment (optional, up to 100 characters)'} placeholder={zh ? '有什么可以改进的？（最多100字符）' : 'What could be improved? (up to 100 characters)'} maxLength={100} value={comment} onChange={event => setComment(event.target.value.slice(0, 100))} rows={3}/>
        </div>
        <footer><button type="button" onClick={dismiss}>{zh ? '取消' : 'Cancel'}</button><button type="submit">{zh ? '提交反馈' : 'Submit feedback'}</button></footer>
      </form>
    </div>
  </div>, document.body);
}

export default memo(function AiAnswerActions({text, messageId, feedbackToken, zh, open, submit}: Props) {
  const [rating, setRating] = useState<Rating>(null);
  const currentRating = useRef<Rating>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {if (!open) setDialogOpen(false);}, [open]);
  function rate(value: Exclude<Rating, null>) {
    if (!messageId || !feedbackToken) return;
    const next = currentRating.current === value ? null : value;
    currentRating.current = next;
    setRating(next);
    setDialogOpen(next === 'dislike');
    submit({messageId, feedbackToken, rating: next});
  }
  return <>
    <div className={styles.answerActions}>
      <button type="button" aria-label={zh ? '复制回答' : 'Copy answer'} title={zh ? '复制回答' : 'Copy answer'} onClick={async () => {
        setCopyError(false);
        try {await navigator.clipboard.writeText(citationMarkdown(text)); setCopied(true); clearTimeout(timer.current); timer.current = setTimeout(() => setCopied(false), 2000);} catch {setCopyError(true);}
      }}><Copy size={15}/>{copied && <span>{zh ? '已复制' : 'Copied'}</span>}</button>
      {copyError && <span role="status">{zh ? '无法复制，请手动选择文本。' : 'Unable to copy. Please select the text.'}</span>}
      {messageId && feedbackToken && <>
        <span>{zh ? '此消息是否有帮助？' : 'Was this message helpful?'}</span>
        <button type="button" className={rating === 'like' ? styles.selected : undefined} aria-pressed={rating === 'like'} aria-label={zh ? '这个回答有帮助' : 'Helpful answer'} onClick={() => rate('like')}><ThumbsUp size={15}/>{zh ? '是' : 'Yes'}</button>
        <button type="button" className={rating === 'dislike' ? styles.selected : undefined} aria-pressed={rating === 'dislike'} aria-label={zh ? '这个回答没有帮助' : 'Unhelpful answer'} onClick={() => rate('dislike')}><ThumbsDown size={15}/>{zh ? '否' : 'No'}</button>
      </>}
    </div>
    {open && dialogOpen && <FeedbackDialog zh={zh} dismiss={() => setDialogOpen(false)} submit={content => {
      setDialogOpen(false);
      if (messageId && feedbackToken) submit({messageId, feedbackToken, rating: 'dislike', content});
    }}/>}
  </>;
});
